import { Client, ClassificationGroup } from '@core/generated/gql-types';
import { calculateMarginChargeRate, formatCurrency } from './clientRates.utils';

export interface MarginRatesTableProps {
  client: Client;
  classificationGroups: ClassificationGroup[];
  tenantMarginPercentage: number | null;
}

export const MarginRatesTable = ({
  client,
  classificationGroups,
  tenantMarginPercentage,
}: MarginRatesTableProps) => (
  <div className="overflow-x-auto">
    <table className="min-w-full border-collapse border border-gray-300">
      <thead>
        <tr className="bg-gray-100">
          <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
            Classification Group
          </th>
          <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
            Classification
          </th>
          <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
            Classification Level
          </th>
          <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
            Country Hash
          </th>
          <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
            Charge Rate (Margin)
          </th>
        </tr>
      </thead>
      <tbody>
        {classificationGroups.flatMap((group) => {
          const groupLevels = (group.classifications ?? []).flatMap(
            (classification) =>
              (classification.classificationLevels ?? []).map((level) => ({
                group,
                classification,
                level,
              })),
          );
          const groupRowSpan = groupLevels.reduce(
            (acc, { level }) => acc + Math.max(1, level.minimums?.length ?? 0),
            0,
          );

          return groupLevels.flatMap(
            ({ group, classification, level }, levelIdx) => {
              const minimums = level.minimums ?? [];
              const rows = minimums.length > 0 ? minimums : [null];
              const levelRowSpan = rows.length;
              const classLevels = classification.classificationLevels ?? [];
              const classRowSpan = classLevels.reduce(
                (acc, levelItem) =>
                  acc + Math.max(1, levelItem.minimums?.length ?? 0),
                0,
              );
              const isFirstInGroup = levelIdx === 0;
              const isFirstInClass = classLevels[0]?.id === level.id;

              return rows.map((minimum, minIdx) => (
                <tr
                  key={`${group.id}-${classification.id}-${level.id}-${minimum?.countryHash ?? 'none'}-${minIdx}`}
                  className="hover:bg-gray-50"
                >
                  {isFirstInGroup && minIdx === 0 && (
                    <td
                      className="border border-gray-300 px-4 py-2 align-top font-semibold"
                      rowSpan={groupRowSpan}
                    >
                      {group.name || '-'}
                    </td>
                  )}
                  {isFirstInClass && minIdx === 0 && (
                    <td
                      className="border border-gray-300 px-4 py-2 pl-8 align-top"
                      rowSpan={classRowSpan}
                    >
                      {classification.name || '-'}
                    </td>
                  )}
                  {minIdx === 0 && (
                    <td
                      className="border border-gray-300 px-4 py-2 pl-16 align-top"
                      rowSpan={levelRowSpan}
                    >
                      {level.name || '-'}
                    </td>
                  )}
                  <td className="border border-gray-300 px-4 py-2 pl-24 align-top">
                    {minimum?.countryHash ?? '-'}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 pl-24 align-top">
                    {formatCurrency(
                      calculateMarginChargeRate(
                        minimum?.minimum,
                        client.marginOverridePercentage ?? undefined,
                        tenantMarginPercentage,
                      ),
                    )}
                  </td>
                </tr>
              ));
            },
          );
        })}
      </tbody>
    </table>
  </div>
);
